/**
 * THE ONE TOKEN SOURCE (CLAUDE.md §8, docs/design/DESIGN_LANGUAGE.md).
 *
 * Three styling systems share this app — AntD for data-dense controls, shadcn/ui for
 * the shell, Tailwind for layout — and all three read their colours from this file:
 *
 *   • Tailwind + shadcn  — `applyThemeTokens` writes these as CSS custom properties on
 *                          <html>; `styles/index.css` maps them to Tailwind's
 *                          `--color-*` namespace under `@theme inline`.
 *   • AntD               — `antdThemeFor` builds the ConfigProvider token overrides
 *                          from the same object.
 *   • First paint        — `tokensCss.ts` generates the pre-paint <style> block and the
 *                          boot script from the same object, injected into index.html by
 *                          a Vite plugin. That is why index.html contains no colours.
 *
 * The CSS file declares the MAPPING; this file declares the VALUES. Change a colour
 * here and every system moves together.
 *
 * ⚠️ THIS IS THE ONLY FILE IN THE REPOSITORY THAT MAY CONTAIN A COLOUR VALUE.
 * ESLint enforces it for `src/components/**` and `src/pages/**`; the generated pre-paint
 * block enforces it for `index.html` and `styles/index.css`. A hex anywhere else is a
 * second source of truth by definition.
 *
 * ─── The design language, in one paragraph ────────────────────────────────────────
 * Dark mode is a near-black ramp: elevation is expressed by surface LIGHTNESS and a
 * hairline border, never by a shadow. The palette is MONOCHROME — `primary` is a neutral
 * (near-white on dark, near-black on light), matching the reference product; the only
 * hues in the system are the four semantic status colours. Text never reaches pure
 * white: 92% over the canvas, because 100% buzzes.
 *
 * Values are hex or rgba(), not oklch, on purpose: AntD parses its seed colours with
 * @ant-design/colors, which understands hex/rgb and not oklch.
 */

/** Both modes, for anything that has to render or iterate every one of them. */
export const THEME_MODES = ['light', 'dark'] as const;

export type ThemeMode = (typeof THEME_MODES)[number];

/**
 * Composites an opaque hex onto itself at `alpha`.
 *
 * The alpha tokens exist so a wash cannot drift from the surface it sits on: every
 * white-alpha value in dark mode derives from the same WHITE constant, so there is one
 * number to change rather than nine.
 */
const rgba = (hex: string, alpha: number): string => {
  const value = Number.parseInt(hex.slice(1), 16);
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
};

const WHITE = '#ffffff';
const BLACK = '#000000';

/**
 * The neutral "brand". There is no accent hue.
 *
 * The reference product is deliberately monochrome — colour is reserved for status, so
 * that when something IS coloured it means something. `primary` inverts across modes,
 * which is what makes a primary button read as the strongest thing on either canvas.
 */
const PRIMARY_LIGHT = '#171717';
const PRIMARY_DARK = '#e5e5e5';

export type SemanticTokens = {
  // ── Surfaces. Always opaque: everything else composites onto them. ───────────────
  /** The canvas the app sits on. */
  background: string;
  /** A panel raised off the canvas: cards, the sidebar, table containers. */
  card: string;
  /** One step further up: popovers, dropdowns, modals, drawers. */
  surfaceRaised: string;
  /** shadcn's name for `surfaceRaised`. */
  popover: string;

  // ── Neutral washes. Always alpha, so they work over any surface. ─────────────────
  hover: string;
  active: string;
  /** shadcn's `accent` means the hover WASH, not a brand colour. See the file header. */
  accent: string;
  muted: string;
  /**
   * A neutral pill at rest — a secondary button, a code chip.
   *
   * Aliased to `hover`, not to `active`, deliberately: a resting fill that already sits
   * at the interaction value has nowhere to go when you point at it.
   */
  secondary: string;

  // ── The text ramp. ──────────────────────────────────────────────────────────────
  foreground: string;
  mutedForeground: string;
  /** Decorative and disabled only — it does not clear 4.5:1 and is not meant to. */
  textTertiary: string;
  cardForeground: string;
  popoverForeground: string;
  accentForeground: string;
  secondaryForeground: string;
  /**
   * An OPAQUE seed for AntD's algorithm, never applied to an element.
   *
   * `darkAlgorithm` does `new FastColor(colorTextBase).setA(0.88)` to derive its neutral
   * ramp. Feeding it `rgba(255,255,255,0.92)` compounds to 0.81 and every derived step
   * drifts. Every visible text step is set explicitly instead.
   */
  textBase: string;

  // ── Borders. AntD's own split: `colorBorder` is controls, `Secondary` is dividers. ─
  borderSubtle: string;
  borderStrong: string;
  border: string;
  input: string;
  /**
   * ─── ⚠️ [UX1 §1f] THE OUTLINE OF AN *EMPTY* SLOT, AND IT IS NOT `border` ──────────────
   *
   *  A dashed ring at `borderSubtle`'s alpha measures about 1.16:1 against the sheet — a hole
   *  an operator has to hunt for, on the one screen whose entire job is finding holes.
   *
   *  ⚠️ IT IS A TOKEN RATHER THAN A CLASS ON `SlotChip`, BECAUSE §1f SAYS SO AND BECAUSE
   *  `border` IS EVERY HAIRLINE IN THE PRODUCT. Darkening that would move nine other things,
   *  and `standardPalette.test.ts` pins `--border-subtle` as a frozen literal.
   *
   *  ⚠️ IT IS NOT HELD TO 3:1, AND THAT IS STATED RATHER THAN QUIETLY OMITTED. Reaching the
   *  non-text bar against white needs roughly 0.42 alpha, which is a heavy box rather than a
   *  hairline and would read as a different design language. NO border in this repository
   *  clears 3:1 and this one must not be the first, or the claim becomes a lie about the rest.
   *  What `contrast.test.ts` asserts instead is that it is strictly stronger than
   *  `borderSubtle` in every palette and mode, with a negative control that it is not an ink —
   *  `textTertiary`'s established pattern.
   *
   *  ⚠️ AND IT IS A DELIBERATE, REVIEWED CHANGE TO `standard`. [C1h]'s freeze is over the
   *  variables that existed then and this is a new one, so no guard fires — which means the
   *  freeze cannot catch it drifting either. `standardPalette.test.ts` pins its literals
   *  separately for that reason.
   */
  slotOutline: string;
  /**
   * ─── ⚠️ [C3c §7] THE ATTENDANCE AXIS — THE FIRST THING IN THIS PRODUCT THAT MEANS SOMETHING
   *     OTHER THAN FULFILMENT ────────────────────────────────────────────────────────────────
   *
   *  Red, amber and green mean NAMED-VERSUS-REQUIRED everywhere else in this app — FR-5.3.1
   *  fixes those four system-wide and `fillState.constant.ts` says every surface uses them.
   *  Attendance is a genuinely different question about the same officer, so it must not be
   *  confusable with the answer to the other one.
   *
   *  ⚠️ SO IT IS A DESATURATED COOL SLATE, NOT A FIFTH HUE. The four status colours are all
   *  heavily saturated; "not saturated" is therefore the fastest statement available of "this is
   *  not a status", and it works before a reader has learned what the colour means. A fifth hue
   *  would have to be told apart from four others by memory.
   *
   *  ⚠️ AND IT IS A **TONE** TOKEN, WHICH IS NOT WHAT §8's "there are no tint TOKENS and none may
   *  be added" BANS. That ban is on a PRE-MIXED WASH of an existing tone — `tintSuccess` as a
   *  second name for `success` at 15% — because the Tailwind opacity modifier already expresses
   *  it and a pre-mixed token cannot follow a `ThemeScope`. This is a tone for a meaning that has
   *  none, consumed through that same modifier at two strengths. Say so at the consumer too, or
   *  the next reader quotes that sentence at this line.
   *
   *  ⚠️ HELD TO NO CONTRAST BAR ITSELF — it is never ink. What IS measured is the text ON its
   *  washes, and that the hue and saturation stay clear of all four status colours;
   *  `web/src/theme/attendanceTint.test.ts` asserts both, in every palette.
   */
  attendance: string;
  /**
   * ─── ⚠️ [C5c §11] A UNIT'S NODAL ACCESS LEVEL — THE ELEVATION MARK'S INK ────────────────────
   *
   *  The small chevron beside a unit's name when its nodal officers act one or two levels up.
   *  It is ICON ink, so it is held to the 3:1 non-text bar against the card and the canvas, and
   *  it must not read as a status: red, amber and green mean named-versus-required, and `info`
   *  is blue — so this is the crest's INDIGO, the colour of "acting at a higher level" (the HQ).
   *  `web/src/theme/elevationTone.test.ts` measures both claims in every palette and mode.
   *
   *  ⚠️ THE SHAPE CARRIES THE LEVEL, NOT THE COLOUR — one chevron for one level up, two for two —
   *  so the mark survives greyscale and a colour-blind reader (`CoverageBar`'s rule).
   */
  elevation: string;
  /**
   * ─── ⚠️ [C3a §9e] THE INTAKE SURFACES' OWN EDGE — AN ALIAS IN `standard`, BRAND IN COLOURFUL ──
   *
   *  §9e asks for colour on the create/edit arrangement page: CHROME AND STRUCTURE, never
   *  adjacent to a number. A card's hairline is the most structural thing on that screen and the
   *  furthest from a figure, so it is where the brand can show without touching the fulfilment
   *  vocabulary (red/amber/green) or the interactive one (blue).
   *
   *  ⚠️ IT ALIASES `border` IN `standard`, WHICH IS WHAT MAKES §9d TRUE BY CONSTRUCTION. [C1h]'s
   *  four-step rule: add a token, give it TODAY's value in `standard`, give it the brand value in
   *  `colourful`, change the component's class once. `standardPalette.test.ts` therefore passes
   *  unedited, and the alias itself is asserted rather than assumed.
   *
   *  ⚠️ IT IS NOT `slotOutline`. That one is `rgba(BLACK, 0.22)` in standard light — a different,
   *  STRONGER value than `border` — so re-pointing a card's hairline at it would have changed
   *  `standard` and failed §9d. Checked by comparing the values, which is the only way to check
   *  it: [C1h] records the login wordmark going from 4.71:1 to 2.68:1 on exactly this mistake.
   *
   *  ⚠️ AND IT IS NOT EVERY CARD'S BORDER. `border` is still the product's hairline and
   *  `standardPalette.test.ts` pins it; this is the INTAKE surfaces' own edge, and widening it
   *  would repaint the whole application from one token nobody re-read.
   *
   *  ⚠️ HELD TO NO CONTRAST BAR, and asserted not to be — `slotOutline`'s argument verbatim.
   *  Reaching the 3:1 non-text bar needs an alpha that makes a hairline a box, and NO border in
   *  this repository clears it. The claim is RELATIVE (stronger than `border` in every palette)
   *  with a negative control that it is not an ink.
   */
  cardOutline: string;
  /**
   * ─── ⚠️ [C2b §6b] THE DASHBOARD TILE'S CATEGORY ACCENT — A HAIRLINE, NOT A WASH ────────
   *
   *  §6b asks for more colour on the dashboard and rules out the canvas: [C1h] over-warmed it
   *  and [UX1] pulled it back to near-white. The opportunity is the tiles, which are uniformly
   *  white with black text — so an accent rule keyed on the tile's EXISTING
   *  `DASHBOARD_TILE_CATEGORIES` value separates the operational half of the grid from the
   *  analytical one without inventing a taxonomy.
   *
   *  ⚠️ NOTHING DECORATIVE LANDS ADJACENT TO A NUMBER, which is §6b's hard constraint and
   *  [P6b §3d]'s standing refusal of per-group hues. Red, amber and green mean FULFILMENT and
   *  blue means INTERACTIVE; a rule on the card's own edge competes with neither, where a
   *  tinted header band behind a figure would.
   *
   *  ⚠️ IN `standard` BOTH ALIAS `border`, SO THE RENDER IS BYTE-IDENTICAL BY CONSTRUCTION
   *  rather than by inspection — [C1h]'s four-step rule. The stylesheet gains two custom
   *  properties and the painted result does not move, which is what keeps the switch a switch.
   *
   *  ⚠️ AND THEY ARE HELD TO NO CONTRAST BAR, for `slotOutline`'s reason stated verbatim one
   *  field up: no border in this repository clears 3:1, and this must not be the first or the
   *  claim becomes a lie about the rest. `colourfulContrast.test.ts` asserts the RELATIVE
   *  claim — each is stronger than `border` in the palette that colours it — with a negative
   *  control that neither is an ink.
   */
  tileAccentOperational: string;
  tileAccentAnalytical: string;

  // ── The neutral brand. ──────────────────────────────────────────────────────────
  primary: string;
  primaryForeground: string;
  /**
   * The primary fill's hover and pressed states, declared rather than derived.
   *
   * AntD would otherwise generate them with @ant-design/colors, which is built for hues:
   * asked to produce a ten-step ramp from a near-white it returns something arbitrary. A
   * button has three states and all three are design decisions.
   */
  primaryHover: string;
  primaryActive: string;
  /** Same value as `primary` today; links are distinguished by an underline, not a hue. */
  link: string;
  ring: string;
  /** The focus halo. A literal string by the time AntD sees it — see antdTheme.ts. */
  ringSoft: string;

  // ── Status. The only hues in the system. ────────────────────────────────────────
  destructive: string;
  destructiveForeground: string;
  success: string;
  warning: string;
  info: string;

  // ── The modal scrim. ────────────────────────────────────────────────────────────
  overlay: string;

  /**
   * ─── ⚠️ [C1b §7] THE DASHBOARD WATERMARK, AND THE ONLY TOKEN HERE WHOSE ALPHA *IS* ITS
   *  DESIGN ──────────────────────────────────────────────────────────────────────────
   *
   *  §7a asks for the crest and the wordmark "both in one muted token" and §7b for "its own
   *  dark value, not the same opacity". One custom property satisfies both LITERALLY: the
   *  crest is a `mask-image` filled with this colour and the wordmark is `color`, so there is
   *  no opacity number anywhere and — crucially — no `dark:` utility, which §8 bans in
   *  `components/` because it is a descendant match that a nested `ThemeScope` cannot beat.
   *
   *  ⚠️ THE TWO ALPHAS ARE MEASURED SEPARATELY BECAUSE WHITE READS LIGHTER THAN BLACK AT
   *  EQUAL ALPHA — the same reason `active` is 0.07 in light and 0.08 in dark, one screen up.
   *  The dashboard is near-black-first, and a crest at the light value on `#0a0a0a` is a grey
   *  smudge rather than a watermark.
   *
   *  ⚠️ IT IS NOT AN INK, AND `contrast.test.ts` ASSERTS THAT IT IS NOT. It is deliberately
   *  far below 3:1 in both themes; reaching for it to tint text would be reaching for the one
   *  value in this file chosen to be almost invisible. `textTertiary`'s negative control is
   *  the precedent for pinning that as a test rather than as a sentence.
   */
  watermark: string;

  /**
   * ─── ⚠️ [C1h §2] THE THREE ALIASING TOKENS, AND THE RULE THAT GOVERNS THEM ────────────
   *
   *  §2a says "if a screen needs a change to look right, the token is wrong — fix the token".
   *  Read literally that is unbuildable: a coloured page heading cannot come from
   *  `foreground`, which is body text on every screen in the product, and a coloured
   *  magnitude bar cannot come from `active`, which is nine other neutral washes.
   *
   *  So the move [C1h] adds, and CLAUDE.md §8 now records: **a colourful-only difference is a
   *  NEW token that ALIASES an existing one in `standard`.** The component's class changes
   *  once (`text-foreground` → `text-heading`), the standard palette gives the new token
   *  exactly the old value, and the rendered page is therefore identical in `standard` BY
   *  CONSTRUCTION rather than by inspection. That is what makes exit criterion 2 provable —
   *  `standardPalette.test.ts` pins both halves.
   *
   *  ⚠️ THE STYLESHEET IS A SUPERSET; THE RENDER IS IDENTICAL. Three more custom properties
   *  are emitted than before this pass. Every one of them resolves to the value the property
   *  it replaced already had, so nothing on screen moves.
   *
   *  ⚠️ AND ADDING A KEY HERE FORCES INERT LINES INTO `pwa/src/theme/palette.ts`, because
   *  `DutyMitraTokens` is a compiler-enforced SUPERSET of this type. That is blessed
   *  precedent — [C1b §7]'s `watermark` did exactly this, and that file's own header says a
   *  shape true in only one app is more expensive than a token inert in one. Duty Mitra
   *  aliases all four and §1f keeps the setting off that surface entirely.
   */

  /**
   * A page or section HEADING. `foreground` in `standard`; the brand's deep violet in
   * `colourful`. ⚠️ It is TEXT, so it clears 4.5:1 on every surface in both palettes —
   * `contrast.test.ts` measures it rather than this comment claiming it.
   */
  heading: string;
  /**
   * A decorative glyph with no data behind it — an empty state's icon, the login mark.
   * `textTertiary` in `standard`; the brand purple in `colourful`.
   *
   * ⚠️ IT CARRIES NO CONTRAST FLOOR, AND THAT IS AN HONEST STATEMENT RATHER THAN AN OMISSION.
   * MEASURED: 2.68:1 on `card` in `standard` and 6.88:1 in `colourful` — a spread of more
   * than 2.5x. Asserting a floor would either fail `standard`, which is frozen and whose
   * empty-state glyphs have always been this faint, or assert something only one palette has
   * to meet. **The rule is a USAGE rule instead: nothing readable may use this token**, which
   * is why it is named for what it draws rather than for how dim it is.
   */
  illustration: string;
  /**
   * The fill behind a MAGNITUDE — `RankedList`'s proportional bar, and nothing else.
   *
   * ⚠️ IT IS NOT A STATUS, WHICH IS THE WHOLE OF WHY §2g PERMITS COLOUR HERE. Red, amber and
   * green mean fulfilment state and `NestedBar`'s bands are tinted from them; a ranking bar
   * is a quantity. `active` in `standard`, so the one genuinely uncoloured graphic on the
   * dashboard stays uncoloured there.
   *
   * ⚠️ AND TEXT SITS ON TOP OF IT, so the wash stays light enough for `foreground` to clear
   * 4.5:1 composited over it — the constraint `ForceGrid`'s 15% balance tints already live
   * under, measured the same way.
   */
  dataBar: string;
};

const darkTokens = (): SemanticTokens => {
  const foreground = rgba(WHITE, 0.92);
  const hover = rgba(WHITE, 0.04);
  const active = rgba(WHITE, 0.08);
  const borderSubtle = rgba(WHITE, 0.07);
  const borderStrong = rgba(WHITE, 0.13);
  const raised = '#1c1c1c';

  return {
    background: '#0a0a0a',
    card: '#141414',
    surfaceRaised: raised,
    popover: raised,

    hover,
    active,
    accent: hover,
    muted: hover,
    secondary: hover,

    foreground,
    mutedForeground: rgba(WHITE, 0.56),
    textTertiary: rgba(WHITE, 0.38),
    cardForeground: foreground,
    popoverForeground: foreground,
    accentForeground: foreground,
    secondaryForeground: foreground,
    textBase: WHITE,

    borderSubtle,
    borderStrong,
    border: borderSubtle,
    input: borderStrong,
    /* §1f — BRIGHTER in dark, where white at equal alpha reads lighter than black does. */
    slotOutline: rgba(WHITE, 0.22),
    /*
     * [C3c §7] Slate 400. Hue 213 against dark `info`'s 199 — 14 degrees of clearance, the
     * narrowest of the four — and saturation ~20% against its ~92%, which is the separator that
     * actually does the work. `attendanceTint.test.ts` measures both rather than trusting this.
     */
    attendance: '#94a3b8',
    /* [C5c §11] Indigo 300: hue ~234, 35 degrees from dark `info`, and bright on the dark card. */
    elevation: '#a5b4fc',
    /* [C3a §9e] THE VARIABLE, not a copied number — the identity is structural, and
       `standardPalette.test.ts` asserts it rather than two literals agreeing by luck. */
    cardOutline: borderSubtle,
    /* ⚠️ [C2b §6b] `standard` ALIASES `border`, in both modes — the render cannot move. */
    tileAccentOperational: borderSubtle,
    tileAccentAnalytical: borderSubtle,

    primary: PRIMARY_DARK,
    primaryForeground: PRIMARY_LIGHT,
    primaryHover: '#f5f5f5',
    primaryActive: '#d4d4d4',
    link: PRIMARY_DARK,
    ring: PRIMARY_DARK,
    ringSoft: rgba(PRIMARY_DARK, 0.35),

    destructive: '#f87171',
    destructiveForeground: '#0a0a0a',
    success: '#4ade80',
    warning: '#fbbf24',
    info: '#38bdf8',

    overlay: rgba(BLACK, 0.66),
    watermark: rgba(WHITE, 0.06),

    /* [C1h §2] The four aliases. Dark is UNCHANGED in both palettes — see `uiPalettes`. */
    heading: foreground,
    illustration: rgba(WHITE, 0.38),
    dataBar: active,
  };
};

const lightTokens = (): SemanticTokens => {
  const foreground = rgba(BLACK, 0.88);
  const hover = rgba(BLACK, 0.04);
  /**
   * 0.07, not the dark side's 0.08. Black at equal alpha reads heavier than white, and
   * it makes light `active` numerically equal to light `borderSubtle`, which is coherent.
   */
  const active = rgba(BLACK, 0.07);
  const borderSubtle = rgba(BLACK, 0.07);
  const borderStrong = rgba(BLACK, 0.13);

  return {
    background: '#fafafa',
    card: WHITE,
    surfaceRaised: WHITE,
    popover: WHITE,

    hover,
    active,
    accent: hover,
    muted: hover,
    secondary: hover,

    foreground,
    mutedForeground: rgba(BLACK, 0.55),
    textTertiary: rgba(BLACK, 0.38),
    cardForeground: foreground,
    popoverForeground: foreground,
    accentForeground: foreground,
    secondaryForeground: foreground,
    textBase: BLACK,

    borderSubtle,
    borderStrong,
    border: borderSubtle,
    input: borderStrong,
    /* §1f — DARKER in light. 0.22 measures ~1.69:1 on `card`, against 0.07's ~1.16. */
    slotOutline: rgba(BLACK, 0.22),
    /*
     * [C3c §7] Slate 600. Hue 215 against light `info`'s 202 — 13 degrees, the narrowest of the
     * four — and saturation ~19% against its ~96%. See the type for why "not saturated" is the
     * statement being made.
     */
    attendance: '#475569',
    /* [C5c §11] Indigo 700 — standard is no longer rendered, and indigo is the answer there too. */
    elevation: '#4338ca',
    /* [C3a §9e] THE VARIABLE, not a copied number — see the dark palette's note. */
    cardOutline: borderSubtle,
    /* ⚠️ [C2b §6b] The alias again. See the type. */
    tileAccentOperational: borderSubtle,
    tileAccentAnalytical: borderSubtle,

    primary: PRIMARY_LIGHT,
    primaryForeground: '#fafafa',
    primaryHover: '#2e2e2e',
    primaryActive: '#000000',
    link: PRIMARY_LIGHT,
    ring: PRIMARY_LIGHT,
    ringSoft: rgba(PRIMARY_LIGHT, 0.35),

    destructive: '#dc2626',
    destructiveForeground: '#fafafa',
    success: '#15803d',
    warning: '#b45309',
    info: '#0369a1',

    overlay: rgba(BLACK, 0.5),
    watermark: rgba(BLACK, 0.045),

    /* [C1h §2] The four aliases, at exactly the values the properties they replace already had. */
    heading: foreground,
    illustration: rgba(BLACK, 0.38),
    dataBar: active,
  };
};

export const themeTokens: Record<ThemeMode, SemanticTokens> = {
  light: lightTokens(),
  dark: darkTokens(),
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C1h §2] THE COLOURFUL PALETTE — AN ALTERNATE SET, NOT AN EDIT.
 *
 *  `standard` above must still render exactly as it did before this pass. That is what makes
 *  §1 a SWITCH rather than a one-way change nobody can undo, and `standardPalette.test.ts`
 *  pins the pre-C1h value of every variable that existed then.
 *
 *  ─── §2d — TWO BRAND HUES, AND THE CREST ONLY SUPPLIES ONE ──────────────────────────
 *
 *  MEASURED: `docs/design/kp-logo.png` is 320x320 with 20 984 opaque pixels, of which
 *  **97% are a single deep indigo** — `#1005a3`, hsl(244 94% 33%). There is no second colour
 *  in the mark to read off, so §2d's second brand colour is DERIVED by rotation rather than
 *  sampled, and the pairing was confirmed with the client.
 *
 *    INDIGO  `#1005a3`  the crest's own value   -> INTERACTIVE: primary, link, ring
 *    PURPLE  `#7e22ce` / `#5b21b6`  ~28 deg off -> CHROME: headings, glyphs, magnitude bars
 *
 *  ⚠️ THERE WAS NO *FIRST* BRAND COLOUR TO BE SECOND TO. `PRIMARY_LIGHT` is `#171717` — the
 *  dashboard's `primary`, `link` and `ring` are all near-black, and its only hues are the
 *  four status colours. §2d's "one accent doing every job" describes a NEUTRAL accent doing
 *  every job, which is why `colourful` gains a hue rather than swapping one.
 *
 *  ⚠️ AND BOTH SIT CLEAR OF EVERY STATUS HUE — destructive hsl(0), warning hsl(32), success
 *  hsl(142), info hsl(201). §2g is not a style rule here: red, amber and green MEAN
 *  fulfilment state and blue means interactive, so a decorative hue that could be mistaken
 *  for one of them is a defect rather than a preference.
 *
 *  ─── §2b — THE CANVAS IS WARMED, AND THE WASHES WITH IT ─────────────────────────────
 *
 *  `standard`'s greys derive from pure `BLACK` over `#fafafa`, which is cool. Every neutral
 *  here derives from `WARM_INK` over a warm off-white instead. ⚠️ THE WASHES HAD TO MOVE TOO:
 *  warming the canvas and leaving `rgba(0,0,0,a)` on top of it produces a grey haze over a
 *  cream page, which reads as dirt rather than as warmth.
 *
 *  ─── §1b — DARK IS THE SAME OBJECT, NOT A COPY ──────────────────────────────────────
 *
 *  ⚠️ `colourful.dark` IS `standard.dark`, REFERENTIALLY. "Dark mode is unchanged in both"
 *  is then a property of the data rather than two literals somebody has to keep in step, and
 *  `uiMode.test.ts` asserts the identity. The cascade says the same thing a second way:
 *  `tokensCss.ts` emits `:root.dark` LAST, so it wins wherever it applies.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** The crest's own indigo, measured off `docs/design/kp-logo.png`. §2d */
const CREST_INDIGO = '#1005a3';
/** ~28 deg off the crest. The chrome hue, and never an interactive one. §2d */
const BRAND_PURPLE = '#7e22ce';
/** The same hue, dark enough to be body-adjacent TEXT. §2c */
const BRAND_PURPLE_DEEP = '#5b21b6';
/** A warm near-black. Every neutral in this palette derives from it. §2b */
const WARM_INK = '#2a2118';

const colourfulLightTokens = (): SemanticTokens => {
  const foreground = rgba(WARM_INK, 0.9);
  const hover = rgba(WARM_INK, 0.04);
  const active = rgba(WARM_INK, 0.07);
  const borderSubtle = rgba(WARM_INK, 0.08);
  const borderStrong = rgba(WARM_INK, 0.15);
  /*
   * ⚠️ [amended UX1 §5] PULLED BACK TO NEAR-WHITE. THE CANVAS WAS `#faf7f2` AND READ YELLOW.
   *
   * MEASURED: `#faf7f2` is hsl(38 44% 96.5%) with an R−B spread of 8, which is enough warmth
   * to read as a colour rather than as a temperature. `#fbfaf8` is a spread of 3.
   *
   * ⚠️ THE SEPARATION FROM THE CARD IS WHAT WAS PROTECTED, AND IT IS NOW STANDARD'S OWN.
   * `standard` lifts `#ffffff` off `#fafafa` at 1.044:1; this pair measures 1.043:1, so a card
   * still leaves the page by the same step the monochrome palette uses. Pulling the canvas back
   * WITHOUT checking that is how a "near-white" canvas becomes a flat one.
   *
   * ⚠️ AND THE CARD GOES TO PURE WHITE, WHICH IS §5's OWN WORDING — "enough to separate the
   * canvas from white cards". The warmth that identifies this palette is carried by the ink,
   * the washes and the brand hue, all unchanged; a card tinted 2 points off white was doing
   * none of that work and was costing the separation above.
   *
   * ⚠️ EVERY CONTRAST FIGURE IMPROVES, WHICH IS THE EXPECTED DIRECTION — lightening a surface
   * raises its ratio against dark ink. `colourfulContrast.test.ts` carries the measurements.
   */
  const card = WHITE;

  return {
    background: '#fbfaf8',
    card,
    surfaceRaised: card,
    popover: card,

    hover,
    active,
    accent: hover,
    muted: hover,
    secondary: hover,

    foreground,
    mutedForeground: rgba(WARM_INK, 0.66),
    textTertiary: rgba(WARM_INK, 0.42),
    cardForeground: foreground,
    popoverForeground: foreground,
    accentForeground: foreground,
    secondaryForeground: foreground,
    textBase: WARM_INK,

    borderSubtle,
    borderStrong,
    border: borderSubtle,
    input: borderStrong,
    /*
     * §1f — DARKER, and carried at 0.25 rather than 0.22 for the reason this palette already
     * carries x1.15 on BOTH its border tokens (0.08 against 0.07, 0.15 against 0.13): warm ink
     * at equal alpha reads lighter than pure black.
     */
    slotOutline: rgba(WARM_INK, 0.25),
    /*
     * ⚠️ [C3c §7] THE SAME VALUE AS `standard`, AND THAT IS DELIBERATE RATHER THAN AN OMISSION.
     *
     * Every other neutral here derives from `WARM_INK`, because warming the canvas and leaving a
     * cold grey on top of it reads as dirt. This is not a neutral: it is the one thing on the
     * screen that must read as NEITHER the brand NOR a status. `colourful` already spends its
     * hues on `BRAND_PURPLE` (271) and `CREST_INDIGO` (244), and a WARM slate would sit a few
     * degrees from `warning` (32) at low saturation — confusable in the other direction. Cool
     * slate at 215 is the largest usable clearance from everything this palette already uses.
     */
    attendance: '#475569',
    /* [C5c §11] The crest's own indigo: acting at a higher level reads as the HQ's colour. */
    elevation: CREST_INDIGO,
    /* [C3a §9e] The brand hairline on the intake surfaces — chrome, never beside a number. */
    cardOutline: rgba(BRAND_PURPLE, 0.22),
    /*
     * ⚠️ [C2b §6b] THE ONE PLACE THE ACCENT IS A COLOUR, AND IT IS THE BRAND'S OWN HUE.
     *
     * Operational takes the brand purple; analytical takes the warm ink it already derives
     * every neutral from, at a stronger alpha than `border`. So the split reads as
     * "this one is about tonight" versus "this one is about the shape of things" rather than
     * as two unrelated colours — and the second half stays a NEUTRAL, which is what keeps the
     * grid from becoming a palette.
     */
    tileAccentOperational: rgba(BRAND_PURPLE, 0.55),
    tileAccentAnalytical: rgba(WARM_INK, 0.22),

    primary: CREST_INDIGO,
    /* ⚠️ [UX1 §5] The canvas colour, as [C1h] set it — an ink on a saturated fill. */
    primaryForeground: '#fbfaf8',
    primaryHover: '#1a0dc4',
    primaryActive: '#0a0378',
    link: CREST_INDIGO,
    ring: CREST_INDIGO,
    ringSoft: rgba(CREST_INDIGO, 0.35),

    /*
     * ⚠️ THE STATUS HUES ARE `standard`'s, UNCHANGED AND DELIBERATELY SO. They are the one
     * part of this palette that MEANS something, and re-tuning them for warmth would move
     * every fulfilment colour in the product to make a canvas look nicer. §2g.
     */
    destructive: '#dc2626',
    destructiveForeground: '#fbfaf8',
    success: '#15803d',
    warning: '#b45309',
    info: '#0369a1',

    overlay: rgba(WARM_INK, 0.5),
    watermark: rgba(CREST_INDIGO, 0.06),

    heading: BRAND_PURPLE_DEEP,
    illustration: BRAND_PURPLE,
    /* A wash, not the hue: `foreground` sits on top of this and must still clear 4.5:1. */
    dataBar: rgba(BRAND_PURPLE, 0.12),
  };
};

/**
 * EVERY PALETTE THIS APP CAN RENDER, keyed on the stored UI mode. [C1h] §1b
 *
 * ⚠️ `standard` IS `themeTokens` ITSELF, NOT A COPY. A copy is a second place a value can be
 * edited and the two would disagree in exactly the release where somebody tidied one of them;
 * `standardPalette.test.ts` asserts the referential identity rather than a deep equality.
 *
 * ⚠️ AND THE KEYS ARE DECLARED STRUCTURALLY RATHER THAN IMPORTED FROM `@pams/shared`. This
 * directory is loaded by BOTH apps' `vite.config.ts` through Node's own TypeScript loader
 * (`configLoader: 'native'`), which resolves no package export maps and would have to
 * type-strip the whole shared barrel to get here — see `index.ts` for the sibling rule about
 * extensions. `UI_MODES` lives in `@pams/shared/constants` for the wire, and
 * `web/src/theme/uiMode.test.ts` pins the two against each other by set equality.
 */
export const uiPalettes = {
  standard: themeTokens,
  colourful: {
    light: colourfulLightTokens(),
    /* ⚠️ §1b: THE SAME OBJECT, so "dark is unchanged in both" cannot drift. */
    dark: themeTokens.dark,
  },
} as const satisfies Record<string, Record<ThemeMode, SemanticTokens>>;

export type UiPaletteName = keyof typeof uiPalettes;

/**
 * Shared shape values, so AntD's `borderRadius` and Tailwind's agree.
 *
 * ONE anchor, `radiusPx`. The other three steps derive from it — Tailwind reaches them
 * through `calc()` in `styles/index.css`, AntD by direct reference in `antdTheme.ts`.
 * `tokens.test.ts` asserts the relations so the two derivations cannot desync.
 *
 * The scale reads: 4 micro chips · 6 small controls and tags · 8 default controls and
 * cards · 10 modals, drawers and table containers. Nothing reaches 16.
 */
export const themeShape = {
  radiusXsPx: 4,
  radiusControlPx: 6,
  radiusPx: 8,
  radiusLargePx: 10,
  controlHeightPx: 38,
  /**
   * ONE family. `Inter Variable` is the name @fontsource-variable/inter declares — not
   * `Inter`, which is the mistake that left this stack silently falling through to
   * `ui-sans-serif` for the whole of P1a.
   */
  fontFamily:
    "'Inter Variable', 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  /**
   * The code-chip face. Deliberately the SYSTEM mono stack and not a webfont: §7's
   * "two font families" anti-pattern is about two display faces, and shipping a second
   * webfont to set a dozen short-code chips is not worth the bytes.
   */
  fontFamilyMono: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  fontSizePx: 14,
} as const;

/**
 * ANY palette that satisfies the shared shape.
 *
 * ⚠️ [amended P5c.0] THERE ARE TWO PALETTES IN THIS REPOSITORY NOW, AND THIS TYPE IS WHAT
 * LETS ONE SET OF GENERATORS SERVE BOTH. `pwa/src/theme/palette.ts` — Duty Mitra, the
 * officer surface — declares `SemanticTokens & {...}`: the same key set plus its own, so
 * the compiler refuses a divergence in EITHER direction. Read that file's header before
 * touching anything below; the values there are measured off a design raster and have
 * nothing to do with the dashboard's monochrome ramp.
 */
export type TokenMap = Readonly<Record<string, string>>;

/** `primaryForeground` -> `--primary-foreground`. */
const toCssVarName = (token: string): string => `--${token.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`;

/**
 * The camelCase -> `--kebab-case` mapping, for any token map.
 *
 * Exported so a second palette gets the SAME mapping rather than a second copy of
 * `toCssVarName` — which is how two apps end up disagreeing about what `--muted-foreground`
 * is called.
 */
export const cssVariablesOf = (tokens: TokenMap, extra: TokenMap = {}): Record<string, string> => ({
  ...Object.fromEntries(Object.entries(tokens).map(([token, value]) => [toCssVarName(token), value])),
  ...extra,
});

/** UNCHANGED SIGNATURE. `web/src/theme/tokens.test.ts` asserts its key count and both modes' names. */
export const cssVariablesFor = (mode: ThemeMode): Record<string, string> =>
  cssVariablesOf(themeTokens[mode], { '--radius': `${themeShape.radiusPx}px` });

/**
 * [C1h §2] The same thing for a NAMED palette.
 *
 * ⚠️ `cssVariablesFor` KEEPS ITS SIGNATURE AND ITS BINDING TO `themeTokens`, deliberately.
 * Every existing caller — `dashboardThemeCss`, `ThemeScope`, `applyThemeTokens` — means the
 * standard palette, and widening the one they call would have made "which palette is this"
 * a question at ten call sites instead of one.
 */
export const cssVariablesForUi = (palette: UiPaletteName, mode: ThemeMode): Record<string, string> =>
  cssVariablesOf(uiPalettes[palette][mode], { '--radius': `${themeShape.radiusPx}px` });

/**
 * Writes the token set onto an element as custom properties.
 *
 * Applied to <html> by `ThemeProvider`, so Tailwind utilities, shadcn components and
 * AntD's cssVar output all resolve against the same values. The `element` parameter is
 * what lets `ThemeScope` render a subtree in the opposite theme — which is how the
 * /design gallery shows both themes on one page.
 */
export const applyThemeTokens = (mode: ThemeMode, element: HTMLElement = document.documentElement): void => {
  applyCssVariables(cssVariablesFor(mode), { dark: mode === 'dark', element });
};

/**
 * ⚠️ [C1h §2] THE SAME WRITE FOR A NAMED PALETTE, AND `ThemeProvider` MUST USE THIS ONE.
 *
 * This is the sharpest edge in the whole of §1e. The pre-paint script picks the palette with
 * a CLASS, and `applyCssVariables` then writes custom properties as INLINE STYLES on
 * `<html>` — which beat every selector, including `:root.ui-colourful`. So a mount that
 * called `applyThemeTokens` would repaint a correctly-painted colourful page back to
 * standard, a few milliseconds in: the exact flash §1e exists to prevent, arriving from the
 * one direction nobody would look.
 *
 * ⚠️ AND `applyThemeTokens` IS KEPT RATHER THAN WIDENED, because `ThemeScope` and the
 * /design gallery genuinely mean the standard palette and a defaulted parameter would put
 * the wrong one in the omitted position — [P5c.0]'s rule for exactly this family of function.
 */
export const applyUiThemeTokens = (
  palette: UiPaletteName,
  mode: ThemeMode,
  element: HTMLElement = document.documentElement,
): void => {
  applyCssVariables(cssVariablesForUi(palette, mode), { dark: mode === 'dark', element });
};

/**
 * The DOM write, with no palette in it.
 *
 * ⚠️ [amended P5c.0] `dark` IS A BOOLEAN PARAMETER RATHER THAN A DERIVED `mode === 'dark'`,
 * AND THAT IS DELIBERATE. Duty Mitra pins light, so `applyDutyMitraTokens` always passes
 * `false` — but the branch stays live CODE that the compiler still checks, rather than
 * statically dead code an editor greys out and the next reader deletes as unreachable.
 * `web/src/lib/surfaces.ts` records that exact trap.
 */
export const applyCssVariables = (
  variables: TokenMap,
  { dark, element = document.documentElement }: { dark: boolean; element?: HTMLElement },
): void => {
  for (const [name, value] of Object.entries(variables)) {
    element.style.setProperty(name, value);
  }
  // `dark` drives shadcn's dark styles and the `@custom-variant dark` declaration.
  element.classList.toggle('dark', dark);
  // `color-scheme` makes native form controls and scrollbars follow the theme too.
  element.style.colorScheme = dark ? 'dark' : 'light';
};
